import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check } from 'lucide-react'

import DialogShell from '@/components/crm/DialogShell'
import {
  confirmationKeys,
  confirmationRefusal,
  dealConfirmation,
  invalidateAfterConfirmation,
  type QueueItem,
} from '@/services/dealConfirmation'
import { stamp } from './ConfirmationViewModel'

/**
 * The manager queue — deals whose confirmed data waits for a manager.
 *
 * Present only while an administrator has switched the step on (Settings →
 * Intake). A manager approves, or returns with a note saying what to look at
 * again; the note is required, because a return that says nothing is a delay.
 * The server holds the four-eyes rule (nobody approves what they submitted) and
 * records the approver and the time; this screen only asks.
 */

function Item({
  item,
  busy,
  onOpenDeal,
  onDecide,
}: {
  item: QueueItem
  busy: boolean
  onOpenDeal: (loanId: string) => void
  onDecide: (loanId: string, kind: 'approve' | 'return', note: string) => void
}) {
  const [returning, setReturning] = useState(false)
  const [note, setNote] = useState('')
  const name = item.property ?? item.loanId
  return (
    <li className="space-y-1.5 border-b border-white/[0.05] px-4 py-2.5" data-loan={item.loanId}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="text-[12px] font-medium text-stone-100">{name}</span>
        <span className="text-[10px] text-stone-500">
          {[item.sponsor, item.type, item.stage].filter(Boolean).join(' · ')}
        </span>
      </div>
      <div className="text-[11px] text-stone-400">
        Confirmed by <span className="text-stone-200">{item.actor}</span>
        {item.at ? ` · ${stamp(item.at)}` : ''}
        {item.fields.length > 0 ? (
          <span className="text-stone-500">
            {' '}
            — {item.fields.length} {item.fields.length === 1 ? 'field' : 'fields'}: {item.fields.slice(0, 6).join(', ')}
            {item.fields.length > 6 ? ', …' : ''}
          </span>
        ) : null}
      </div>
      {returning ? (
        <div className="space-y-1.5">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label={`Note for ${name}`}
            rows={2}
            placeholder="What should they look at again?"
            className="w-full rounded border border-stone-700 bg-black/40 px-2 py-1 text-[11px] text-stone-100 outline-none placeholder:text-stone-600 focus:border-amber-300/50"
          />
          <div className="flex gap-1.5">
            <button
              type="button"
              disabled={busy || !note.trim()}
              onClick={() => onDecide(item.loanId, 'return', note)}
              className="rounded bg-amber-300 px-2.5 py-1 text-[10px] font-medium text-black disabled:opacity-50"
            >
              Return with note
            </button>
            <button
              type="button"
              onClick={() => setReturning(false)}
              className="rounded border border-stone-700 px-2.5 py-1 text-[10px] text-stone-300"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => onOpenDeal(item.loanId)}
            className="rounded border border-stone-600 px-2.5 py-1 text-[10px] text-stone-200 hover:bg-white/5"
          >
            Open
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide(item.loanId, 'approve', '')}
            aria-label={`Approve ${name}`}
            className="rounded bg-emerald-400 px-2.5 py-1 text-[10px] font-medium text-black disabled:opacity-50"
          >
            Approve
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => setReturning(true)}
            aria-label={`Return ${name}`}
            className="rounded border border-amber-300/40 px-2.5 py-1 text-[10px] text-amber-200 disabled:opacity-50"
          >
            Return…
          </button>
        </div>
      )}
    </li>
  )
}

export function ConfirmationViewQueueDialog({
  onClose,
  onOpenDeal,
}: {
  onClose: () => void
  onOpenDeal: (loanId: string) => void
}) {
  const queryClient = useQueryClient()
  const queue = useQuery({ queryKey: confirmationKeys.queue, queryFn: () => dealConfirmation.queue() })
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  const decide = async (loanId: string, kind: 'approve' | 'return', note: string) => {
    setBusy(true)
    setNotice(null)
    try {
      if (kind === 'approve') await dealConfirmation.approve(loanId)
      else await dealConfirmation.returnToConfirmer(loanId, note)
      await invalidateAfterConfirmation(queryClient, loanId)
      setNotice({ tone: 'ok', text: kind === 'approve' ? 'Approved — recorded under your name.' : 'Returned with your note.' })
    } catch (error) {
      setNotice({ tone: 'error', text: confirmationRefusal(error).message })
    } finally {
      setBusy(false)
    }
  }

  const data = queue.data
  return (
    <DialogShell label="Manager queue" width="2xl" zIndex={150} onClose={onClose}>
      {notice ? (
        <div
          role={notice.tone === 'error' ? 'alert' : 'status'}
          className={`flex items-start gap-2 border-b px-4 py-2 text-[11px] ${
            notice.tone === 'error'
              ? 'border-red-400/20 bg-red-400/[0.06] text-red-200'
              : 'border-emerald-400/15 bg-emerald-400/[0.05] text-emerald-200'
          }`}
        >
          {notice.tone === 'error' ? <AlertTriangle className="mt-px h-3.5 w-3.5" /> : <Check className="mt-px h-3.5 w-3.5" />}
          <span>{notice.text}</span>
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto scrollbar-thin">
        {queue.isLoading ? (
          <p className="px-4 py-6 text-[12px] text-stone-500">Reading the queue…</p>
        ) : queue.isError || !data ? (
          <p className="px-4 py-6 text-[12px] text-red-300">The queue could not be read. Nothing has been changed.</p>
        ) : !data.enabled ? (
          <p className="px-4 py-6 text-[12px] text-stone-400">
            The manager queue is switched off. An administrator turns it on in Settings → Intake; until then a
            person’s confirmation stands on its own, with who and when recorded.
          </p>
        ) : !data.canApprove ? (
          <p className="px-4 py-6 text-[12px] text-stone-400">
            Only an account that can approve (Intake · approve) sees what is waiting.
          </p>
        ) : (
          <>
            <div className="px-4 pb-1 pt-3 font-mono text-[9px] uppercase tracking-widest text-stone-500">
              Awaiting approval · {data.waiting.length}
            </div>
            {data.waiting.length === 0 ? (
              <p className="px-4 pb-4 text-[12px] text-stone-500">Nothing is waiting for a manager.</p>
            ) : (
              <ul>
                {data.waiting.map((item) => (
                  <Item key={item.id} item={item} busy={busy} onOpenDeal={onOpenDeal} onDecide={decide} />
                ))}
              </ul>
            )}
            {data.recent.length > 0 ? (
              <>
                <div className="px-4 pb-1 pt-4 font-mono text-[9px] uppercase tracking-widest text-stone-500">
                  Recently decided
                </div>
                <ul className="pb-3">
                  {data.recent.map((row) => (
                    <li key={row.id} className="px-4 py-1 text-[11px] text-stone-400">
                      <span className="text-stone-200">{row.property ?? row.loanId}</span>{' '}
                      {row.state === 'approved' ? 'approved' : 'returned'} by {row.actor}
                      {row.at ? ` · ${stamp(row.at)}` : ''}
                      {row.note ? <span className="text-stone-500"> — {row.note}</span> : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}
      </div>
    </DialogShell>
  )
}
